// Listen mode — records short spoken segments from the mic and transcribes
// them via OpenAI Whisper (server-side). The browser's own SpeechRecognition
// produced gibberish in game environments, so we capture audio with a simple
// energy-based voice-activity detector (VAD): when speech is detected we
// record; after a beat of silence we cut the segment and send it off. Whisper
// returns the text, which the caller shows on-screen and forwards to the
// announcer if it's worth saying.
//
// Only TEXT ever leaves after transcription; audio segments are short and
// ephemeral. The caller pauses us while the announcer is talking so the mic
// never captures its own voice.

export function listenSupported(): boolean {
  return (
    typeof navigator !== "undefined" &&
    !!navigator.mediaDevices?.getUserMedia &&
    typeof window !== "undefined" &&
    typeof window.MediaRecorder !== "undefined"
  );
}

function pickMimeType(): string {
  const types = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/ogg;codecs=opus",
    "audio/mp4",
    "audio/mp4;codecs=mp4a.40.2",
  ];
  for (const t of types) {
    try {
      if (window.MediaRecorder.isTypeSupported(t)) return t;
    } catch {
      /* ignore */
    }
  }
  return "";
}

// Raw capture — no echo-cancel / noise-suppress / AGC, which would make the
// browser grab the "voice communication" mic source and pull output off
// Bluetooth A2DP. We gate the mic while the announcer talks, so we don't
// need AEC.
const RAW_AUDIO: MediaTrackConstraints = {
  echoCancellation: false,
  noiseSuppression: false,
  autoGainControl: false,
};

const MIC_ID_KEY = "cornholio.micDeviceId";

function safeGet(k: string): string | null {
  try {
    return localStorage.getItem(k);
  } catch {
    return null;
  }
}
function safeSet(k: string, v: string) {
  try {
    localStorage.setItem(k, v);
  } catch {
    /* ignore */
  }
}

// Pick the built-in (non-Bluetooth) mic from an enumerated device list, so
// the OS uses the PHONE's mic and leaves the Bluetooth speaker in high-
// quality A2DP output (we only want Bluetooth for the audience, not its
// mic). Returns undefined if we can't tell (labels need prior permission).
function pickLocalMicId(devices: MediaDeviceInfo[]): string | undefined {
  const bt = /bluetooth|headset|hands.?free|airpod|earbud|wireless|\bbt\b|\bsco\b/i;
  const pseudo = new Set(["", "default", "communications"]);
  const real = devices.filter(
    (d) => d.kind === "audioinput" && !pseudo.has(d.deviceId),
  );
  const nonBt = real.filter((d) => !bt.test(d.label || ""));
  const builtin = nonBt.find((d) =>
    /built.?in|internal|phone|device/i.test(d.label || ""),
  );
  return (builtin ?? nonBt[0])?.deviceId;
}

// Open the local (built-in) mic. Tries a cached device id first (no blip);
// otherwise opens the default to earn permission, finds the built-in mic,
// and re-opens on it. Throws on permission denial.
async function openLocalMic(): Promise<MediaStream> {
  const cached = safeGet(MIC_ID_KEY);
  if (cached) {
    try {
      return await navigator.mediaDevices.getUserMedia({
        audio: { deviceId: { exact: cached }, ...RAW_AUDIO },
      });
    } catch {
      /* stale id — fall through to re-discover */
    }
  }
  let stream = await navigator.mediaDevices.getUserMedia({ audio: RAW_AUDIO });
  let localId: string | undefined;
  try {
    localId = pickLocalMicId(await navigator.mediaDevices.enumerateDevices());
  } catch {
    localId = undefined;
  }
  const curId = stream.getAudioTracks()[0]?.getSettings().deviceId;
  if (localId && localId !== curId) {
    stream.getTracks().forEach((t) => t.stop());
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: { deviceId: { exact: localId }, ...RAW_AUDIO },
      });
    } catch {
      stream = await navigator.mediaDevices.getUserMedia({ audio: RAW_AUDIO });
    }
  }
  const finalId =
    localId ?? stream.getAudioTracks()[0]?.getSettings().deviceId;
  if (finalId) safeSet(MIC_ID_KEY, finalId);
  return stream;
}

export type Listener = {
  start: () => void;
  stop: () => void;
  pause: () => void;
  resume: () => void;
};

export function createListener(opts: {
  // Silence (ms) after speech before a segment is cut and sent.
  silenceMs?: number;
  // Ignore blips shorter than this (ms) — coughs, clicks.
  minSpeechMs?: number;
  onUtterance: (text: string) => void;
  onTranscript?: (text: string) => void;
  onRecording?: (active: boolean) => void;
  onError?: (error: string) => void;
  // Transcribe a segment. `worth` = a server-side mini-model judged it
  // worth sending to the announcer; `text` is the (cleaned) transcript to
  // display either way.
  transcribe: (blob: Blob) => Promise<{ text: string; worth: boolean }>;
}): Listener {
  const silenceMs = opts.silenceMs ?? 1200;
  const minSpeechMs = opts.minSpeechMs ?? 400;

  let active = false;
  let paused = false;
  let stream: MediaStream | null = null;
  let audioCtx: AudioContext | null = null;
  let analyser: AnalyserNode | null = null;
  let recorder: MediaRecorder | null = null;
  let chunks: Blob[] = [];
  let mimeType = "audio/webm";

  let speaking = false;
  let segStart = 0;
  let silenceStart = 0;
  let noiseFloor = 0.01;
  let vadTimer: ReturnType<typeof setInterval> | null = null;
  let buf: Uint8Array<ArrayBuffer> | null = null;
  let discardNext = false; // drop the current segment (e.g. paused mid-record)

  const now = () => performance.now();

  const rms = (): number => {
    if (!analyser || !buf) return 0;
    analyser.getByteTimeDomainData(buf);
    let sum = 0;
    for (let i = 0; i < buf.length; i++) {
      const v = (buf[i]! - 128) / 128;
      sum += v * v;
    }
    return Math.sqrt(sum / buf.length);
  };

  const startSeg = () => {
    if (!recorder || recorder.state !== "inactive") return;
    chunks = [];
    segStart = now();
    try {
      recorder.start();
    } catch {
      /* ignore */
    }
  };

  const endSeg = () => {
    if (!recorder || recorder.state !== "recording") return;
    try {
      recorder.stop(); // fires onstop → handleSegment
    } catch {
      /* ignore */
    }
  };

  const handleSegment = async (blob: Blob, durationMs: number) => {
    if (durationMs < minSpeechMs || blob.size < 1400) return;
    try {
      const { text, worth } = await opts.transcribe(blob);
      if (!text) return;
      opts.onTranscript?.(text); // show what was heard, sent or not
      if (worth) opts.onUtterance(text); // mini-model said it's worth it
      // Clear the on-screen field a moment after showing it.
      window.setTimeout(() => opts.onTranscript?.(""), 2600);
    } catch {
      /* transcription failed — degrade silently */
    }
  };

  const tick = () => {
    if (!active || paused) return;
    const level = rms();
    const threshold = Math.max(0.02, noiseFloor * 2.4 + 0.012);
    if (level > threshold) {
      if (!speaking) {
        speaking = true;
        opts.onRecording?.(true);
        startSeg();
      }
      silenceStart = 0;
    } else {
      // Adapt the noise floor only while it's quiet.
      noiseFloor = noiseFloor * 0.95 + level * 0.05;
      if (speaking) {
        if (silenceStart === 0) silenceStart = now();
        else if (now() - silenceStart >= silenceMs) {
          speaking = false;
          silenceStart = 0;
          opts.onRecording?.(false);
          endSeg();
        }
      }
    }
  };

  const setup = async () => {
    try {
      // Capture on the PHONE's built-in mic (never the Bluetooth mic), so
      // the Bluetooth speaker stays in high-quality A2DP output for the
      // audience. We only ever want Bluetooth for output, not its mic.
      stream = await openLocalMic();
    } catch (e) {
      active = false;
      opts.onError?.((e as Error).name || "not-allowed");
      return;
    }
    if (!active) {
      // Toggled off while the permission prompt was up.
      stream.getTracks().forEach((t) => t.stop());
      stream = null;
      return;
    }
    const Ctx =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext: typeof AudioContext })
        .webkitAudioContext;
    audioCtx = new Ctx();
    // Created after the async getUserMedia, so the gesture may be gone and
    // the context can start suspended — resume so the analyser gets audio.
    void audioCtx.resume().catch(() => {});
    const src = audioCtx.createMediaStreamSource(stream);
    analyser = audioCtx.createAnalyser();
    analyser.fftSize = 1024;
    buf = new Uint8Array(analyser.fftSize);
    src.connect(analyser);

    mimeType = pickMimeType();
    recorder = mimeType
      ? new MediaRecorder(stream, { mimeType })
      : new MediaRecorder(stream);
    recorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) chunks.push(e.data);
    };
    recorder.onstop = () => {
      const type = recorder?.mimeType || mimeType || "audio/webm";
      const blob = new Blob(chunks, { type });
      const dur = now() - segStart;
      chunks = [];
      if (discardNext) {
        discardNext = false;
        return; // paused mid-segment — don't transcribe the announcer
      }
      void handleSegment(blob, dur);
    };

    vadTimer = setInterval(tick, 80);
  };

  const teardown = () => {
    if (vadTimer) {
      clearInterval(vadTimer);
      vadTimer = null;
    }
    speaking = false;
    silenceStart = 0;
    if (recorder && recorder.state !== "inactive") {
      try {
        recorder.onstop = null;
        recorder.stop();
      } catch {
        /* ignore */
      }
    }
    recorder = null;
    chunks = [];
    opts.onRecording?.(false);
    opts.onTranscript?.("");
    if (stream) {
      stream.getTracks().forEach((t) => t.stop());
      stream = null;
    }
    if (audioCtx) {
      void audioCtx.close().catch(() => {});
      audioCtx = null;
    }
    analyser = null;
    buf = null;
  };

  return {
    start() {
      if (active) return;
      active = true;
      paused = false;
      void setup();
    },
    stop() {
      active = false;
      paused = false;
      teardown();
    },
    pause() {
      if (!active || paused) return;
      paused = true;
      // Drop any in-progress segment (so we never transcribe the announcer)
      // but keep the stream + recorder so resume() is instant (no re-prompt).
      speaking = false;
      silenceStart = 0;
      opts.onRecording?.(false);
      if (recorder && recorder.state === "recording") {
        discardNext = true;
        try {
          recorder.stop();
        } catch {
          /* ignore */
        }
      }
      opts.onTranscript?.("");
    },
    resume() {
      if (!active || !paused) return;
      paused = false;
    },
  };
}
